/**
 * Sidebar entries the finance lanes contribute.
 *
 * Finance was one group of twenty screens; it is five now, one per app, so the
 * menu says what a company would be buying. Four more entries — the financial
 * statements and their layouts, period end and period close — are exported
 * separately and joined to the Accounting group, because closing a period is
 * ledger work that only ever sat here by naming accident.
 *
 * Every entry stays visible whether or not its app is installed. Opening one a
 * company does not have lands on that app's intro rather than nothing.
 */
import {
  BadgeCheck,
  Banknote,
  BellRing,
  BookCheck,
  Building2,
  CalendarCheck,
  CalendarClock,
  CreditCard,
  FileBarChart,
  FileMinus,
  FilePlus2,
  Landmark,
  LayoutList,
  Percent,
  PieChart,
  Receipt,
  Scale,
  SlidersHorizontal,
  Wallet,
} from "lucide-react";

import type { NavEntry } from "@/app-shell/route-registry";

export const financeNavEntries: NavEntry[] = [
  {
    path: "/finance/customer-invoices",
    labelKey: "apps.receivables.name",
    icon: Banknote,
    app: "receivables",
    children: [
      {
        path: "/finance/customer-invoices",
        labelKey: "finance.nav.customerInvoices",
        icon: FilePlus2,
        permission: "finance.customer-invoice.read",
      },
      {
        path: "/finance/customer-receipts/new",
        labelKey: "finance.nav.customerReceipts",
        icon: Receipt,
        permission: "finance.customer-receipt.create",
      },
      {
        path: "/finance/collections",
        labelKey: "finance.nav.collections",
        icon: BellRing,
        permission: "finance.dunning.read",
      },
      {
        path: "/finance/gateway",
        labelKey: "finance.nav.gateway",
        icon: CreditCard,
        permission: "finance.gateway-payment.read",
      },
      {
        path: "/finance/reports/aging",
        labelKey: "finance.nav.aging",
        icon: PieChart,
        permission: "finance.ar-aging.read",
      },
    ],
  },
  {
    path: "/finance/supplier-invoices",
    labelKey: "apps.payables.name",
    icon: Wallet,
    app: "payables",
    children: [
      {
        path: "/finance/supplier-invoices",
        labelKey: "finance.nav.supplierInvoices",
        icon: FileMinus,
        permission: "finance.supplier-invoice.read",
      },
      {
        path: "/finance/payment-runs",
        labelKey: "finance.nav.paymentRuns",
        icon: Banknote,
        permission: "finance.payment-run.read",
      },
    ],
  },
  {
    path: "/finance/bank-accounts",
    labelKey: "apps.banking.name",
    icon: Landmark,
    app: "banking",
    children: [
      {
        path: "/finance/bank-accounts",
        labelKey: "finance.nav.bankAccounts",
        icon: Building2,
        permission: "finance.bank-account.read",
      },
      {
        path: "/finance/bank-statements",
        labelKey: "finance.nav.bankStatements",
        icon: Wallet,
        permission: "finance.bank-statement.read",
      },
      {
        path: "/finance/bank-reconciliation",
        labelKey: "finance.nav.reconciliation",
        icon: Scale,
        permission: "finance.bank-statement.read",
      },
      {
        path: "/finance/bank-rules",
        labelKey: "finance.nav.bankRules",
        icon: SlidersHorizontal,
        permission: "finance.bank-rule.read",
      },
    ],
  },
  {
    path: "/finance/fixed-assets",
    labelKey: "apps.fixed_assets.name",
    icon: Building2,
    app: "fixed_assets",
    children: [
      {
        path: "/finance/fixed-assets",
        labelKey: "finance.nav.fixedAssets",
        icon: BookCheck,
        permission: "finance.fixed-asset.read",
      },
      {
        path: "/finance/asset-classes",
        labelKey: "finance.nav.assetClasses",
        icon: BookCheck,
        permission: "finance.asset-class.read",
      },
      {
        path: "/finance/depreciation-runs",
        labelKey: "finance.nav.depreciationRuns",
        icon: CalendarCheck,
        permission: "finance.depreciation-run.read",
      },
    ],
  },
  {
    path: "/finance/tax-codes",
    labelKey: "apps.tax.name",
    icon: Receipt,
    app: "tax",
    children: [
      {
        path: "/finance/tax-codes",
        labelKey: "finance.nav.taxCodes",
        icon: Percent,
        permission: "finance.tax-code.read",
      },
      {
        path: "/finance/proofs",
        labelKey: "finance.nav.proofs",
        icon: BadgeCheck,
        permission: "finance.tax-return.read",
      },
    ],
  },
];

/**
 * The four screens that moved to Accounting. Exported rather than grouped here
 * so the shell can join them to the Accounting group; a company that has
 * Accounting has these, whether or not it bought any of the finance apps.
 */
export const financeAccountingNavEntries: NavEntry[] = [
  {
    path: "/finance/statements",
    labelKey: "finance.nav.statements",
    icon: FileBarChart,
    permission: "finance.statement.read",
  },
  {
    path: "/finance/statement-layouts",
    labelKey: "finance.nav.statementLayouts",
    icon: LayoutList,
    permission: "finance.statement-layout.read",
  },
  {
    path: "/finance/period-end",
    labelKey: "finance.nav.periodEnd",
    icon: CalendarClock,
    permission: "finance.recurring-journal.read",
  },
  {
    path: "/finance/period-close",
    labelKey: "finance.nav.periodClose",
    icon: CalendarCheck,
    permission: "finance.period-close.read",
  },
];
